import { $applyNodeReplacement, TextNode } from 'lexical';
import type { EditorConfig, LexicalNode, NodeKey, SerializedTextNode, Spread } from 'lexical';

export type SerializedFileMentionNode = Spread<{ path: string }, SerializedTextNode>;

function basename(path: string): string {
  return path.split('/').pop() ?? path;
}

/** An inline, atomic reference to a file in the session's sandbox, inserted by the `@`-mention
 * dropdown. Carries only the path — the agent has full sandbox access and reads the file itself,
 * so there's nothing else for this node to hold. */
export class FileMentionNode extends TextNode {
  __path: string;

  static getType(): string {
    return 'file-mention';
  }

  static clone(node: FileMentionNode): FileMentionNode {
    return new FileMentionNode(node.__path, node.__text, node.__key);
  }

  static importJSON(serializedNode: SerializedFileMentionNode): FileMentionNode {
    return $createFileMentionNode(serializedNode.path).updateFromJSON(serializedNode);
  }

  constructor(path: string, text?: string, key?: NodeKey) {
    super(text ?? `@${basename(path)}`, key);
    this.__path = path;
    // Deleted/selected as one unit — a mention has no meaningful character-by-character editing.
    this.setMode('token');
  }

  exportJSON(): SerializedFileMentionNode {
    return { ...super.exportJSON(), path: this.__path };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className = 'rounded-md bg-brand-tint px-1 py-px font-mono text-brand-text';
    return dom;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  getPath(): string {
    return this.getLatest().__path;
  }
}

export function $createFileMentionNode(path: string): FileMentionNode {
  return $applyNodeReplacement(new FileMentionNode(path));
}

export function $isFileMentionNode(node: LexicalNode | null | undefined): node is FileMentionNode {
  return node instanceof FileMentionNode;
}
